import * as i3 from '@angular/cdk/portal';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import * as i2$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Component, Inject, HostBinding, Injectable, Optional, Injector, EventEmitter, ChangeDetectionStrategy, Input, Output, NgModule, Pipe } from '@angular/core';
import { Validators, UntypedFormControl, UntypedFormGroup, UntypedFormArray, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@ngneat/transloco';
import { TranslocoModule } from '@ngneat/transloco';
import { has, get } from 'json-pointer';
import { throwError, of, combineLatest } from 'rxjs';
import { tap, catchError, switchMap, map, startWith } from 'rxjs/operators';
import * as i2 from '@angular/material/snack-bar';
import { safeEval, toLabel } from '@jaspero/utils';
import { moveItemInArray } from '@angular/cdk/drag-drop';

const CUSTOM_COMPONENT_DATA = new InjectionToken('CUSTOM_COMPONENT_DATA');

class CustomComponent {
    constructor(data) {
        this.data = data;
        const classes = [
            `fb-field-${this.data.columnsDesktop || 12}`
        ];
        if (this.data.columnsTablet) {
            classes.push(`m-fb-field-${this.data.columnsTablet}`);
        }
        if (this.data.columnsMobile) {
            classes.push(`s-fb-field-${this.data.columnsMobile}`);
        }
        if (this.data.class) {
            classes.push(this.data.class);
        }
        this.class = classes.join(' ');
    }
}
CustomComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: CustomComponent, deps: [{ token: CUSTOM_COMPONENT_DATA }], target: i0.ɵɵFactoryTarget.Component });
CustomComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.4", type: CustomComponent, selector: "fb-custom-component", host: { properties: { "class": "this.class" } }, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: CustomComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fb-custom-component',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CUSTOM_COMPONENT_DATA]
                }] }]; }, propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });

const COMPONENT_DATA = new InjectionToken('COMPONENT_DATA');

// @dynamic
class FieldComponent {
    constructor(cData) {
        this.cData = cData;
        const classes = [
            `fb-field-${this.cData.columnsDesktop || 12}`
        ];
        if (this.cData.columnsTablet) {
            classes.push(`m-fb-field-${this.cData.columnsTablet}`);
        }
        if (this.cData.columnsMobile) {
            classes.push(`s-fb-field-${this.cData.columnsMobile}`);
        }
        if (this.cData.class) {
            classes.push(this.cData.class);
        }
        this.class = classes.join(' ');
    }
}
FieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FieldComponent, deps: [{ token: COMPONENT_DATA }], target: i0.ɵɵFactoryTarget.Component });
FieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.4", type: FieldComponent, selector: "fb-field", host: { properties: { "class": "this.class" } }, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fb-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [COMPONENT_DATA]
                }] }]; }, propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });

const CUSTOM_COMPONENTS = new InjectionToken('CUSTOM_COMPONENTS');

class FormBuilderContextService {
    constructor(injectedComponents) {
        this.injectedComponents = injectedComponents;
        this.fields = {};
        this.segments = {};
        this.components = {};
    }
    get componentMap() {
        return {
            ...this.injectedComponents || {},
            ...this.components
        };
    }
    registerField(name, component) {
        if (this.fields.hasOwnProperty(name)) {
            throw new Error(`Field with selector ${name} already registered.`);
        }
        this.fields[name] = component;
    }
    registerSegment(selector, component) {
        if (this.segments.hasOwnProperty(selector)) {
            throw new Error(`Segment with selector ${selector} already registered.`);
        }
        this.segments[selector] = component;
    }
    registerComponent(selector, component) {
        if (this.components.hasOwnProperty(selector)) {
            throw new Error(`Field with selector ${selector} already registered.`);
        }
        this.components[selector] = component;
    }
}
FormBuilderContextService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderContextService, deps: [{ token: CUSTOM_COMPONENTS, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
FormBuilderContextService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderContextService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderContextService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CUSTOM_COMPONENTS]
                }] }]; } });

var State;
(function (State) {
    State["Create"] = "create";
    State["Edit"] = "edit";
})(State || (State = {}));

class FormBuilderService {
    constructor(transloco, snackBar) {
        this.transloco = transloco;
        this.snackBar = snackBar;
    }
    notify(options = {}) {
        const finalOptions = {
            success: 'OPERATION_COMPLETED',
            error: 'OPERATION_FAILED',
            ...options
        };
        return (source$) => {
            return source$.pipe(tap(() => {
                if (finalOptions.success) {
                    this.snackBar.open(this.transloco.translate(finalOptions.success), this.transloco.translate('DISMISS'), {
                        duration: 5000
                    });
                }
            }), catchError(err => {
                if (finalOptions.error) {
                    this.snackBar.open(this.transloco.translate(finalOptions.error), this.transloco.translate('DISMISS'), {
                        panelClass: 'snack-bar-error',
                        duration: 5000
                    });
                }
                console.error(err);
                return throwError(err);
            }));
        };
    }
}
FormBuilderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderService, deps: [{ token: i1.TranslocoService }, { token: i2.MatSnackBar }], target: i0.ɵɵFactoryTarget.Injectable });
FormBuilderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.TranslocoService }, { type: i2.MatSnackBar }]; } });

const DEFAULT_SEGMENT = new InjectionToken('DEFAULT_SEGMENT');

const SEGMENT_DATA = new InjectionToken('SEGMENT_DATA');

var ConditionType;
(function (ConditionType) {
    ConditionType["Function"] = "function";
    ConditionType["Statement"] = "statement";
})(ConditionType || (ConditionType = {}));
var ConditionAction;
(function (ConditionAction) {
    ConditionAction["Show"] = "show";
    ConditionAction["Hide"] = "hide";
})(ConditionAction || (ConditionAction = {}));
var ConditionEvaluate;
(function (ConditionEvaluate) {
    ConditionEvaluate["OnLoad"] = "onLoad";
    ConditionEvaluate["OnChange"] = "onChange";
})(ConditionEvaluate || (ConditionEvaluate = {}));

function compileFields(config) {
    const { parser, definitions, fields, pointers = null, mutateKey = (key) => key, formId, parentForm } = config;
    return (fields || [])
        .reduce((acc, keyObject) => {
        let condition;
        let key = keyObject;
        if (keyObject?.constructor === Object) {
            const { field, action, deps } = keyObject;
            condition = { field };
            switch (action?.constructor) {
                case Object:
                    condition.action = [action];
                    break;
                case Array:
                    condition.action = action;
                    break;
                default:
                    condition.action = [{}];
                    break;
            }
            condition.action.forEach((item) => {
                item.type = item.type || 'show';
                item.eval = safeEval(item.eval) || null;
            });
            condition.deps = deps || [];
            key = mutateKey(field);
        }
        else {
            key = mutateKey(key);
        }
        const definition = parser.getFromDefinitions(key, definitions);
        if (!definition ||
            !definition.roles ||
            (typeof definition.roles === 'string' ?
                definition.roles === parser.role :
                definition.roles.includes(parser.role))) {
            acc.push(parser.field({
                pointerKey: key,
                pointer: (pointers || parser.pointers)[key],
                definitions,
                single: true,
                condition,
                formId,
                parentForm
            }));
        }
        return acc;
    }, []);
}

function compileSegment(config) {
    const { segment, parser, definitions, injector, value = {}, parent, formId, parentForm, index } = config;
    const classes = [];
    let fields = [];
    let customComponents = [];
    if (segment.columnsDesktop) {
        classes.push(`col-${segment.columnsDesktop}`);
    }
    if (segment.columnsTablet) {
        classes.push(`col-m-${segment.columnsTablet}`);
    }
    if (segment.columnsMobile) {
        classes.push(`col-s-${segment.columnsMobile}`);
    }
    /**
     * If there aren't any column definitions
     * default to full width
     */
    if (!classes.length) {
        classes.push(parent ? 'fb-field-12' : 'col-12');
    }
    if (segment.classes) {
        classes.push(...segment.classes);
    }
    if (segment.fields) {
        /**
         * If it's an array fields aren't parsed
         */
        if (segment.array) {
            // @ts-ignore
            fields = (segment.fields || []).map(fi => {
                if (fi.constructor === Object) {
                    return {
                        ...fi,
                        segment: segment.array
                    };
                }
                else {
                    return segment.array + fi;
                }
            });
        }
        else {
            fields = compileFields({
                parser,
                definitions,
                fields: segment.fields,
                formId,
                parentForm
            });
        }
    }
    if (segment.components) {
        const { componentMap } = injector.get(FormBuilderContextService);
        if (componentMap) {
            customComponents = segment.components.reduce((acc, component) => {
                const comp = componentMap[component.selector];
                const id = parser.form.get('id');
                if (comp && (!component.authorization || component.authorization.includes(parser.role))) {
                    acc.push(new ComponentPortal(comp, null, Injector.create({
                        providers: [{ provide: CUSTOM_COMPONENT_DATA, useValue: {
                                    form: parser.form,
                                    parentForm,
                                    formId,
                                    ...id && { id: id.value },
                                    ...(component.input || {})
                                } }],
                        parent: injector
                    })));
                }
                return acc;
            }, []);
        }
    }
    const compiledSegment = {
        ...segment,
        classes,
        fields,
        entryValue: value,
        customComponents
    };
    /**
     * TODO:
     * Statement support
     */
    if (segment.conditions) {
        compiledSegment.conditions = [];
        for (const cur of segment.conditions) {
            let condition;
            const type = cur.type || ConditionType.Function;
            const action = cur.action || ConditionAction.Show;
            const evaluateOn = cur.evaluateOn || ConditionEvaluate.OnLoad;
            const evaluateStates = cur.evaluateStates || [0, 1, 2];
            if (cur.condition) {
                condition = safeEval(cur.condition);
            }
            if (condition) {
                /**
                 * Evaluate on load conditions
                 */
                if (evaluateOn === ConditionEvaluate.OnLoad) {
                    /**
                     * Check if items should be excluded right away
                     */
                    if ((action === ConditionAction.Show && !condition(value)) ||
                        (action === ConditionAction.Hide && condition(value))) {
                        return null;
                    }
                }
                compiledSegment.conditions.push({
                    condition,
                    type,
                    action,
                    evaluateStates,
                    evaluateOn,
                    ...cur.data && {
                        data: cur.data
                    }
                });
            }
        }
    }
    const ctx = injector.get(FormBuilderContextService);
    let defaultSegment = 'card';
    try {
        defaultSegment = injector.get(DEFAULT_SEGMENT);
    }
    catch (e) { }
    return {
        component: new ComponentPortal(ctx.segments[segment.type || defaultSegment], null, Injector.create({
            providers: [{ provide: SEGMENT_DATA, useValue: {
                        segment: compiledSegment,
                        parser,
                        definitions,
                        parentForm,
                        formId,
                        ...parent && { parent },
                        ...index !== undefined && { index }
                    } }],
            parent: injector
        })),
        ...compiledSegment
    };
}

function filterAndCompileSegments(config) {
    const { segments, parser, ...segmentConfig } = config;
    return segments.reduce((acc, segment) => {
        if (!segment.authorization || segment.authorization.includes(parser.role)) {
            const compiled = compileSegment({
                segment,
                parser,
                ...segmentConfig,
            });
            /**
             * TODO:
             * Exclude any segments that end up not having
             * a single field or component
             */
            if (compiled) {
                acc.push(
                // @ts-ignore
                compiled);
            }
        }
        return acc;
    }, []);
}

var SchemaType;
(function (SchemaType) {
    SchemaType["Object"] = "object";
    SchemaType["Array"] = "array";
    // Simple
    SchemaType["String"] = "string";
    SchemaType["Number"] = "number";
    SchemaType["Integer"] = "integer";
    SchemaType["Boolean"] = "boolean";
    SchemaType["Null"] = "null";
})(SchemaType || (SchemaType = {}));

// @dynamic
class SchemaValidators {
    static multipleOf(num) {
        return (control) => {
            return control.value % num === 0 ? null : { multipleOf: num };
        };
    }
}

const CUSTOM_FIELDS = new InjectionToken('CUSTOM_FIELDS');

/**
 * TODO:
 * We need a better approach for defaults.
 * With the fields being delivered as a separate
 * module there is no guaranty that an "input"
 * field will exist
 */
function schemaToComponent(schemaType) {
    switch (schemaType) {
        case SchemaType.String:
            return {
                type: 'input',
                configuration: {
                    type: 'text'
                }
            };
        case SchemaType.Number:
        case SchemaType.Integer:
            return {
                type: 'input',
                configuration: {
                    type: 'number'
                }
            };
        case SchemaType.Boolean:
            return {
                type: 'checkbox'
            };
        case SchemaType.Array:
            return {
                type: 'chips'
            };
    }
}

// @dynamic
class Parser {
    constructor(schema, injector, state, role, definitions = {}, customFields) {
        this.schema = schema;
        this.injector = injector;
        this.state = state;
        this.role = role;
        this.definitions = definitions;
        this.pointers = {};
        this.customFields = {};
        if (customFields) {
            this.customFields = customFields;
            return;
        }
        let custom;
        let ctxFields;
        try {
            custom = this.injector.get(CUSTOM_FIELDS);
        }
        catch (e) { }
        try {
            const ctx = this.injector.get(FormBuilderContextService);
            ctxFields = ctx.fields || {};
        }
        catch (e) { }
        this.customFields = {
            ...custom || {},
            ...ctxFields || {}
        };
    }
    static standardizeKey(key) {
        if (key[0] === '/') {
            key = key.slice(1);
        }
        return key;
    }
    static standardizeKeyWithSlash(key) {
        if (key[0] !== '/') {
            key = '/' + key;
        }
        return key;
    }
    static stringControl(definition, required) {
        const controlValidation = [];
        const validation = {};
        if (required) {
            controlValidation.push(Validators.required);
            validation.required = true;
        }
        if (definition.maxLength) {
            controlValidation.push(Validators.maxLength(definition.maxLength));
            validation.maxLength = definition.maxLength;
        }
        if (definition.minLength) {
            controlValidation.push(Validators.minLength(definition.minLength));
            validation.minLength = definition.minLength;
        }
        if (definition.pattern) {
            controlValidation.push(Validators.pattern(definition.pattern));
            validation.patter = definition.pattern;
        }
        return {
            control: new UntypedFormControl(definition.default || '', controlValidation),
            validation
        };
    }
    static numberControl(definition, required) {
        const validation = {};
        const controlValidation = [];
        if (required) {
            controlValidation.push(Validators.required);
            validation.required = true;
        }
        if (definition.minimum) {
            const minimum = definition.minimum + (definition.exclusiveMinimum ? 1 : 0);
            controlValidation.push(Validators.min(minimum));
            validation.minimum = minimum;
        }
        if (definition.maximum) {
            const maximum = definition.maximum - (definition.exclusiveMaximum ? 1 : 0);
            controlValidation.push(Validators.max(maximum));
            validation.maximum = maximum;
        }
        if (definition.multipleOf) {
            controlValidation.push(SchemaValidators.multipleOf(definition.multipleOf));
            validation.multipleOf = definition.multipleOf;
        }
        return {
            control: new UntypedFormControl(definition.default || null, controlValidation),
            validation
        };
    }
    static booleanControl(definition, required) {
        const controlValidation = [];
        const validation = {};
        if (required) {
            controlValidation.push(Validators.required);
            validation.required = true;
        }
        return {
            control: new UntypedFormControl(definition.hasOwnProperty('default') ? definition.default : false, controlValidation),
            validation
        };
    }
    buildForm(value, required = [], base = '/', addId = true) {
        const properties = this.buildProperties(this.schema.properties || {}, required || this.schema.required, base, addId, value);
        this.form = properties.form;
        this.pointers = properties.pointers;
        if (value) {
            this.form.patchValue(value);
        }
        return this.form;
    }
    buildProperties(properties, required = [], base = '/', addId = true, entryValue = null) {
        const { form, pointers } = [
            ...Object.entries(properties),
            /**
             * Add the id field as a property so that
             * it can be added to the form if needed
             */
            ...(addId
                ? [
                    [
                        'id',
                        {
                            type: 'string'
                        }
                    ]
                ]
                : [])
        ].reduce((group, [key, value]) => {
            const isRequired = required.includes(key);
            const pointerKey = base + key;
            const definition = this.getFromDefinitions(pointerKey) || {};
            let parsed;
            switch (value.type) {
                case SchemaType.String:
                    parsed = Parser.stringControl(value, isRequired);
                    break;
                case SchemaType.Number:
                case SchemaType.Integer:
                    parsed = Parser.numberControl(value, isRequired);
                    break;
                case SchemaType.Boolean:
                    parsed = Parser.booleanControl(value, isRequired);
                    break;
                case SchemaType.Object:
                    if (value.properties && Object.keys(value.properties).length) {
                        const objectProperties = this.buildProperties(value.properties, value.required, pointerKey + '/', false);
                        for (const added in objectProperties.pointers) {
                            if (objectProperties.pointers.hasOwnProperty(added)) {
                                // @ts-ignore
                                group.pointers[added] = objectProperties.pointers[added];
                            }
                        }
                        parsed = {
                            control: objectProperties.form,
                            validation: {}
                        };
                    }
                    /**
                     * When properties aren't defined we create a
                     * FormControl instead of a FormGroup
                     */
                    else {
                        parsed = Parser.stringControl(value, isRequired);
                    }
                    break;
                case SchemaType.Array:
                    parsed = this.buildArray(base, value);
                    break;
            }
            // @ts-ignore
            group.form[key] = parsed.control;
            // @ts-ignore
            group.pointers[pointerKey] = {
                key,
                type: value.type,
                ...definition.formatOnLoad
                    && { formatOnLoad: safeEval(definition.formatOnLoad) },
                ...definition.formatOnSave
                    && { formatOnSave: safeEval(definition.formatOnSave) },
                ...definition.formatOnCreate
                    && { formatOnCreate: safeEval(definition.formatOnCreate) },
                ...definition.formatOnEdit
                    && { formatOnEdit: safeEval(definition.formatOnEdit) },
                // @ts-ignore
                ...parsed
            };
            /**
             * Creat all FormArray controls
             */
            if (value.type === SchemaType.Array) {
                this.populateArray(pointerKey, entryValue, group.pointers);
            }
            return group;
        }, {
            form: {},
            pointers: {}
        });
        return {
            pointers,
            form: new UntypedFormGroup(form)
        };
    }
    /**
     * @param pointerKey Lookup key for the pointer
     * @param pointer Entire pointer object that should be used
     * @param definitions Entire definitions object that should be used
     * @param single Defines if the field shown in the form or in the table
     * @param condition Evaluate function which validates provided action ('show' | 'hide')
     */
    field(config) {
        const { pointerKey, pointer, definitions = {}, single = true, condition, formId, parentForm } = config;
        if (!pointer) {
            throw new Error(`Couldn't find pointer for ${pointerKey}.`);
        }
        const { key, type, control, validation } = pointer;
        const definition = {
            ...this.getFromDefinitions(pointerKey, definitions)
        };
        if (definition.label === undefined) {
            definition.label = toLabel(key);
        }
        if ((definition.disableOn &&
            (Array.isArray(definition.disableOn) ?
                definition.disableOn.includes(this.state)
                : definition.disableOn === this.state)) ||
            (definition.disableForRoles &&
                (Array.isArray(definition.disableForRoles) ?
                    definition.disableForRoles.includes(this.role)
                    : definition.disableForRoles === this.role))) {
            control.disable();
        }
        /**
         * We don't show labels in the table
         */
        if (!single) {
            definition.label = '';
        }
        if (!definition.component) {
            definition.component = schemaToComponent(type);
        }
        // @ts-ignore
        const component = this.customFields[definition.component.type];
        if (!component) {
            // @ts-ignore
            throw new Error(`Couldn't find a component defined for type: ${definition.component.type}`);
        }
        const configuration = safeEval(definition.component.configuration || {});
        const safeConfiguration = typeof configuration === 'function' ? configuration() : configuration;
        const portal = new ComponentPortal(component, null, Injector.create({
            providers: [{
                    provide: COMPONENT_DATA, useValue: {
                        control,
                        validation,
                        single,
                        pointers: this.pointers,
                        form: this.form,
                        formId,
                        parentForm,
                        pointer: pointerKey,
                        ...definition,
                        ...safeConfiguration
                    }
                }],
            parent: this.injector
        }));
        return {
            pointer: pointerKey,
            control,
            portal,
            validation,
            placeholder: definition.placeholder || '',
            label: definition.label,
            onlyOn: definition.onlyOn,
            condition
        };
    }
    addArrayItem(pointer, target, loadHooks = false, reverse = true) {
        const operation = reverse ? 'unshift' : 'push';
        const control = target.control;
        if (target.arrayType === SchemaType.Array ||
            target.arrayType === SchemaType.Object) {
            const properties = this.buildProperties(target.properties, target.required, pointer + '/', false);
            if (loadHooks) {
                this.loadHooks(properties.pointers);
            }
            // @ts-ignore
            target.arrayPointers[operation](properties.pointers);
            if (reverse) {
                control.insert(0, properties.form);
            }
            else {
                control.push(properties.form);
            }
            return properties.pointers;
        }
        else {
            const cont = new UntypedFormControl('');
            if (control instanceof UntypedFormArray) {
                if (reverse) {
                    control.insert(0, cont);
                }
                else {
                    control.push(cont);
                }
            }
            return cont;
        }
    }
    moveArrayItem(target, fromIndex, toIndex) {
        if (target.arrayType === SchemaType.Array ||
            target.arrayType === SchemaType.Object) {
            moveItemInArray(target.arrayPointers, fromIndex, toIndex);
        }
        const control = target.control;
        const currentGroup = control.at(fromIndex);
        control.removeAt(fromIndex);
        control.insert(toIndex, currentGroup);
    }
    removeArrayItem(target, index) {
        target.arrayPointers.splice(index, 1);
        target.control.removeAt(index);
    }
    loadHooks(pointers = this.pointers) {
        Object.values(pointers).forEach(entry => {
            /**
             * TODO:
             * For the moment formatOn methods are
             * only supported on FormControls.
             * We might want to expand on this later on.
             */
            if (entry.control instanceof UntypedFormControl && entry.formatOnLoad) {
                const adjustedValue = entry.formatOnLoad(entry.control.value);
                if (adjustedValue !== entry.control.value) {
                    entry.control.setValue(adjustedValue);
                }
            }
        });
    }
    preSaveHooks(currentState, statesToProcess = [State.Create, State.Edit], pointers = this.pointers) {
        const preSaveData = this.form.getRawValue();
        Object.values(pointers).forEach(entry => {
            /**
             * TODO:
             * For the moment formatOn methods are
             * only supported on FormControls.
             * We might want to expand on this later on.
             */
            if (entry.control instanceof UntypedFormControl) {
                let value = entry.control.value;
                if (entry.formatOnSave) {
                    value = entry.formatOnSave(value, preSaveData);
                }
                if (statesToProcess.includes(currentState)) {
                    if (currentState === State.Edit && entry.formatOnEdit) {
                        value = entry.formatOnEdit(value, preSaveData);
                    }
                    else if (entry.formatOnCreate) {
                        value = entry.formatOnCreate(value, preSaveData);
                    }
                }
                if (value !== entry.control.value) {
                    entry.control.setValue(value);
                }
            }
            if (entry.arrayPointers) {
                entry.arrayPointers.forEach(arrayPointers => this.preSaveHooks(currentState, statesToProcess, arrayPointers));
            }
        });
    }
    getFromDefinitions(key, definitions = this.definitions) {
        return definitions[Parser.standardizeKey(key)];
    }
    /**
     * TODO:
     * - Handle contains case
     * - Handle items or contains as array not object
     */
    buildArray(base, definition) {
        if (definition.items) {
            return {
                arrayType: definition.items.type,
                properties: definition.items.properties,
                required: definition.items.required,
                validation: {},
                control: new UntypedFormArray([]),
                arrayPointers: []
            };
        }
        else {
            return {
                control: new UntypedFormControl([]),
                arrayType: SchemaType.String,
                validation: {}
            };
        }
    }
    populateArray(pointer, entryValue, pointers) {
        if (has(entryValue, pointer)) {
            const values = get(entryValue, pointer);
            if (Array.isArray(values) && values.length) {
                values.forEach(v => {
                    const items = this.addArrayItem(pointer, pointers[pointer], false, false);
                    for (const key in items) {
                        const itemVal = items[key];
                        if (!itemVal || !itemVal.arrayPointers) {
                            continue;
                        }
                        this.populateArray(key, pointer
                            .split('/')
                            .reverse()
                            .reduce((acc, cur) => {
                            if (!cur) {
                                return acc;
                            }
                            return { [Parser.standardizeKey(cur)]: acc };
                        }, v), { [key]: itemVal });
                    }
                });
            }
        }
    }
    copy(overrides = {}) {
        const cp = new Parser(overrides.schema || this.schema, overrides.injector || this.injector, overrides.state || this.state, overrides.role || this.role, overrides.definitions || this.definitions, overrides.customFields || this.customFields);
        ['form', 'pointers'].forEach(key => {
            if (overrides[key]) {
                cp[key] = overrides[key];
            }
        });
        cp.parent = this;
        return cp;
    }
}

const ROLE = new InjectionToken('ROLE');

class FormBuilderComponent {
    constructor(service, injector, role, defaultSegment, cdr) {
        this.service = service;
        this.injector = injector;
        this.role = role;
        this.defaultSegment = defaultSegment;
        this.cdr = cdr;
        this.state = State.Create;
        this.clearOnClose = true;
        this.id = 'jp-fb-main';
        this.valueChanges = new EventEmitter();
        this.validityChanges = new EventEmitter();
        this.rendered = new EventEmitter();
    }
    ngOnChanges(changes) {
        /**
         * In case the provider is provided internally
         * and the id has changed we delete it here
         * before going in to rendering to force the
         * creation of a new parser
         */
        if (changes.id && (changes.id.currentValue !== changes.id.previousValue)) {
            delete this.innerParser;
        }
        if (changes.parser?.currentValue) {
            this.innerParser = changes.parser.currentValue;
        }
        if (changes.data) {
            this.render();
        }
        if (changes.value && this.form) {
            this.form.patchValue(changes.value.currentValue);
        }
    }
    ngOnDestroy() {
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
        }
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
        }
        if (this.clearOnClose) {
            try {
                delete window.jpFb.forms[this.id];
                delete window.jpFb.parsers[this.id];
                delete window.jpFb.operations[this.id];
            }
            catch (e) { }
        }
    }
    process() {
        this.innerParser.preSaveHooks(this.state);
        return this.form.getRawValue();
    }
    save(collectionId, documentId) {
        const data = this.form.getRawValue();
        /**
         * Child forms processes are run on save
         * of the parent form
         */
        if (this.parent) {
            return of(data);
        }
        const processes = Object.entries(window.jpFb.operations[this.id])
            .sort((p1, p2) => p1[1].priority - p2[1].priority)
            .filter(process => process[1].save);
        if (!processes.length) {
            return of(data);
        }
        const operations = [
            ...processes.map(([pointer, process]) => switchMap(() => process.save({
                cData: process.cData,
                pointer,
                collectionId,
                documentId,
                entryValue: this.value,
                outputValue: data
            }))),
            map(() => data)
        ];
        return of(data).pipe(...operations);
    }
    saveAndProcess(collectionId, documentId) {
        this.process();
        return this.save(collectionId, documentId);
    }
    validate(data) {
        this.data = data;
        try {
            this.render();
            return {
                error: false,
                message: ''
            };
        }
        catch (error) {
            return {
                error: true,
                message: error.message || 'Invalid Schema provided!'
            };
        }
    }
    render() {
        const value = this.data.value || {};
        const definitions = this.data.definitions || {};
        if (!this.innerParser) {
            this.innerParser = new Parser(this.data.schema, this.injector, this.state, this.role, definitions);
            this.form = this.innerParser.buildForm(value, null, '/', false);
        }
        else {
            this.form = this.innerParser.form;
        }
        if (!window.jpFb) {
            window.jpFb = {
                assignOperation: config => {
                    const { cData, ...operation } = config;
                    const parentForm = cData.parentForm || {};
                    const formId = parentForm?.id || cData.formId || 'jp-fb-main';
                    const path = parentForm?.pointer ?
                        (parentForm.pointer + cData.pointer) :
                        cData.pointer;
                    window.jpFb.operations[formId][path] = {
                        priority: 1,
                        cData,
                        ...operation
                    };
                },
                exists: config => {
                    let value;
                    try {
                        value = get(config.outputValue, config.pointer);
                    }
                    catch (e) {
                        return { exists: false };
                    }
                    return { value, exists: true };
                },
                change: (config) => {
                    if (!config.entryValue) {
                        return true;
                    }
                    let originalValue;
                    try {
                        originalValue = get(config.entryValue, config.pointer);
                    }
                    catch (e) {
                        return true;
                    }
                    let currentValue;
                    try {
                        currentValue = get(config.outputValue, config.pointer);
                    }
                    catch (e) {
                        return false;
                    }
                    return currentValue !== originalValue;
                },
                forms: {},
                parsers: {},
                operations: {}
            };
        }
        window.jpFb.forms[this.id] = this.form;
        // @ts-ignore
        window.jpFb.parsers[this.id] = this.innerParser;
        /**
         * Child forms push operations to their parent form
         */
        if (!this.parent) {
            window.jpFb.operations[this.id] = {};
        }
        this.innerParser.loadHooks();
        this.segments = filterAndCompileSegments({
            segments: this.data.segments || [{
                    title: '',
                    fields: Object.keys(this.innerParser.pointers),
                    columnsDesktop: 12,
                    type: this.defaultSegment || 'empty'
                }],
            parser: this.innerParser,
            definitions,
            injector: this.injector,
            value,
            formId: this.id,
            parentForm: this.parent
        });
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
        }
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
        }
        this.changeSubscription = this.form.valueChanges
            .subscribe(val => {
            this.valueChanges.emit(val);
        });
        this.statusSubscription = this.form.statusChanges
            .subscribe(val => {
            this.validityChanges.emit(val === 'VALID');
        });
        this.cdr.markForCheck();
        this.rendered.emit();
    }
}
FormBuilderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderComponent, deps: [{ token: FormBuilderService }, { token: i0.Injector }, { token: ROLE, optional: true }, { token: DEFAULT_SEGMENT, optional: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FormBuilderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.4", type: FormBuilderComponent, selector: "fb-form-builder", inputs: { data: "data", value: "value", parser: "parser", state: "state", clearOnClose: "clearOnClose", id: "id", parent: "parent" }, outputs: { valueChanges: "valueChanges", validityChanges: "validityChanges", rendered: "rendered" }, host: { properties: { "id": "this.id" } }, providers: [FormBuilderService], usesOnChanges: true, ngImport: i0, template: "<div class=\"grid\">\n  <ng-container *ngFor=\"let segment of segments\">\n    <ng-template [cdkPortalOutlet]=\"segment.component\"></ng-template>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i2$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fb-form-builder', changeDetection: ChangeDetectionStrategy.OnPush, providers: [FormBuilderService], template: "<div class=\"grid\">\n  <ng-container *ngFor=\"let segment of segments\">\n    <ng-template [cdkPortalOutlet]=\"segment.component\"></ng-template>\n  </ng-container>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: FormBuilderService }, { type: i0.Injector }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ROLE]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DEFAULT_SEGMENT]
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { data: [{
                type: Input
            }], value: [{
                type: Input
            }], parser: [{
                type: Input
            }], state: [{
                type: Input
            }], clearOnClose: [{
                type: Input
            }], id: [{
                type: HostBinding,
                args: ['id']
            }, {
                type: Input
            }], parent: [{
                type: Input
            }], valueChanges: [{
                type: Output
            }], validityChanges: [{
                type: Output
            }], rendered: [{
                type: Output
            }] } });

class SegmentComponent {
    constructor(sData, injector) {
        this.sData = sData;
        this.injector = injector;
        this.nestedArraySegments = [];
        this.arrayFields = [];
    }
    get parentIndex() {
        if (!this.sData.parent) {
            return 0;
        }
        return this.sData.parser.parent.pointers[this.sData.parent].arrayPointers.indexOf(this.pointers);
    }
    ngOnInit() {
        this.segment = this.sData.segment;
        this.arrayConfiguration = {
            add: true,
            remove: true,
            sort: true,
            reverse: true,
            ...this.segment.arrayConfiguration || {}
        };
        this.classes = this.sData.segment.classes.join(' ');
        this.pointers = this.sData.parser.pointers;
        this.arrayPointer = this.pointers[(this.sData.parent || '') + this.segment.array];
        this.id = this.sData.segment.id || '';
        this.components = this.segment.customComponents || [];
        /**
         * Each segment compiles all nested segments
         */
        this.nestedSegments = this.compileNestedSegments(this.sData.segment.nestedSegments);
        const { arrayFields, nestedArraySegments } = this.populateArrayFields(this.segment);
        this.arrayFields = arrayFields;
        this.nestedArraySegments = nestedArraySegments;
    }
    addArrayItem(loadHook = true, index) {
        const operation = this.arrayConfiguration.reverse ? 'unshift' : 'push';
        // debugger;
        const { arrayFields, nestedArraySegments } = this.generateArrayFieldsAndSegments(index || (this.arrayConfiguration.reverse ? 0 : this.nestedArraySegments.length), this.segment, this.sData.parser.addArrayItem((this.sData.parent || '') + this.segment.array, this.arrayPointer, loadHook, this.arrayConfiguration.reverse));
        this.arrayFields[operation](arrayFields);
        this.nestedArraySegments[operation](nestedArraySegments);
    }
    moveArray(up, fromIndex) {
        let toIndex;
        if (up) {
            toIndex = fromIndex === 0 ? this.arrayFields.length - 1 : fromIndex - 1;
        }
        else {
            toIndex = fromIndex === (this.arrayFields.length - 1) ? 0 : fromIndex + 1;
        }
        moveItemInArray(this.arrayFields, fromIndex, toIndex);
        moveItemInArray(this.nestedArraySegments, fromIndex, toIndex);
        this.sData.parser.moveArrayItem(this.arrayPointer, fromIndex, toIndex);
    }
    removeArrayItem(index) {
        this.sData.parser.removeArrayItem(this.arrayPointer, index);
        this.nestedArraySegments.splice(index, 1);
        this.arrayFields.splice(index, 1);
    }
    generateArrayFields(pointers, segment = this.segment) {
        if (!segment.fields?.length) {
            return [];
        }
        return compileFields({
            parser: this.sData.parser,
            definitions: this.sData.definitions,
            fields: segment.fields,
            pointers,
            mutateKey: key => (this.sData.parent || '') + key,
            formId: this.sData.formId,
            parentForm: this.sData.parentForm,
        });
    }
    compileNestedSegments(segments) {
        let parser = this.sData.parser;
        if (this.sData.parent) {
            parser = parser.copy({
                pointers: this.pointers[this.sData.parent + this.sData.segment.array].arrayPointers[this.sData.index]
            });
        }
        return filterAndCompileSegments({
            segments: (segments || []).map(item => {
                if (!item.classes?.length) {
                    item.classes = ['fb-field-12'];
                }
                return item;
            }) || [],
            parser,
            definitions: this.sData.definitions,
            injector: this.injector,
            value: this.segment.entryValue,
            formId: this.sData.formId,
            parentForm: this.sData.parentForm
        });
    }
    populateArrayFields(segment) {
        const arrayFields = [];
        const nestedArraySegments = [];
        /**
         * Populate array fields
         */
        if (segment.array &&
            this.segment.entryValue &&
            has(this.segment.entryValue, segment.array)) {
            const values = get(this.segment.entryValue, segment.array);
            values.forEach((value, index) => {
                const generated = this.generateArrayFieldsAndSegments(index, segment, undefined, value);
                arrayFields.push(generated.arrayFields);
                nestedArraySegments.push(generated.nestedArraySegments);
            });
            for (let i = 0; i < values.length; i++) {
                const pointers = this.pointers[(this.sData.parent || '') + segment.array].arrayPointers[i];
                this.sData.parser.loadHooks(pointers);
            }
        }
        return {
            arrayFields,
            nestedArraySegments
        };
    }
    generateArrayFieldsAndSegments(index, segment, pointers, value) {
        if (!pointers) {
            pointers = this.pointers[(this.sData.parent || '') + segment.array].arrayPointers[index];
        }
        const parser = this.sData.parser.copy({ pointers });
        const arrayFields = this.generateArrayFields(pointers, segment);
        const nestedArraySegments = filterAndCompileSegments({
            segments: this.sData.segment.nestedSegments || [],
            parser,
            definitions: this.sData.definitions,
            injector: this.injector,
            ...value && { value },
            parent: (this.sData.parent || '') + segment.array,
            index,
            formId: this.sData.formId,
            parentForm: this.sData.parentForm
        });
        return { arrayFields, nestedArraySegments };
    }
}
SegmentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: SegmentComponent, deps: [{ token: SEGMENT_DATA }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
SegmentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.4", type: SegmentComponent, selector: "fb-segment", host: { properties: { "class": "this.classes", "id": "this.id" } }, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: SegmentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fb-segment',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [SEGMENT_DATA]
                }] }, { type: i0.Injector }]; }, propDecorators: { classes: [{
                type: HostBinding,
                args: ['class']
            }], id: [{
                type: HostBinding,
                args: ['id']
            }] } });

class FormBuilderModule {
    static forRoot() {
        return {
            ngModule: FormBuilderModule,
            providers: [FormBuilderContextService]
        };
    }
}
FormBuilderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FormBuilderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderModule, declarations: [SegmentComponent,
        FieldComponent,
        FormBuilderComponent,
        CustomComponent], imports: [CommonModule,
        ReactiveFormsModule,
        /**
         * External
         */
        PortalModule,
        TranslocoModule], exports: [FormBuilderComponent] });
FormBuilderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderModule, imports: [CommonModule,
        ReactiveFormsModule,
        /**
         * External
         */
        PortalModule,
        TranslocoModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: FormBuilderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SegmentComponent,
                        FieldComponent,
                        FormBuilderComponent,
                        CustomComponent
                    ],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        /**
                         * External
                         */
                        PortalModule,
                        TranslocoModule
                    ],
                    exports: [FormBuilderComponent]
                }]
        }] });

class ShowFieldPipe {
    transform(fields, parser, index) {
        const changes = fields.reduce((filtered, field) => {
            if (field.condition) {
                filtered.push((field.condition.deps.length ?
                    combineLatest(field.condition.deps.map(dep => this.getListener(dep, parser, index))) :
                    this.getListener('form', parser, index))
                    .pipe(map(() => field), startWith(null), map(() => this.checkField(field, parser, index) ? field : null)));
            }
            else {
                filtered.push(this.checkField(field, parser, index) ? of(field) : of(null));
            }
            return filtered;
        }, []);
        return combineLatest(changes).pipe(map((items) => items.filter(it => it)));
    }
    getListener(control, parser, valueIndex) {
        if (control === 'form') {
            return parser.form.valueChanges;
        }
        const fields = control.split('/').filter(it => it);
        let pointer;
        for (let i = 0; i < fields.length; i++) {
            const path = fields.map((item, index) => index > i ? '' : ('/' + item)).join('');
            const field = '/' + fields[i];
            if (!pointer) {
                pointer = parser.pointers[field];
            }
            else {
                pointer = pointer.arrayPointers[valueIndex || 0][path];
            }
        }
        return pointer.control.valueChanges;
    }
    checkField(field, parser, index) {
        return (!field.onlyOn ||
            (Array.isArray(field.onlyOn) ?
                field.onlyOn.includes(parser.state) :
                field.onlyOn === parser.state)) && this.checkCondition(field.condition, parser, index);
    }
    checkCondition(condition, parser, index) {
        if (!condition) {
            return true;
        }
        const row = parser.form.getRawValue();
        let response = true;
        condition.action.forEach(action => {
            if (!action.eval) {
                return;
            }
            let valid = false;
            try {
                valid = action.eval(row, index || 0);
            }
            catch (error) { }
            switch (action.type) {
                case 'show': {
                    response = valid;
                    break;
                }
                case 'hide': {
                    response = !valid;
                    break;
                }
                case 'set-to': {
                    if (valid) {
                        const value = safeEval(action.configuration.value);
                        parser.pointers[condition.field].control.setValue(typeof value === 'function' ? value(row, index || 0) : value, { emitEvent: false });
                    }
                    break;
                }
                default: {
                    response = true;
                    break;
                }
            }
        });
        return response;
    }
}
ShowFieldPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: ShowFieldPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ShowFieldPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: ShowFieldPipe, name: "showField" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: ShowFieldPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'showField' }]
        }] });

class ShowFieldModule {
}
ShowFieldModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: ShowFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ShowFieldModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.4", ngImport: i0, type: ShowFieldModule, declarations: [ShowFieldPipe], imports: [CommonModule], exports: [ShowFieldPipe] });
ShowFieldModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: ShowFieldModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.4", ngImport: i0, type: ShowFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ShowFieldPipe],
                    imports: [CommonModule],
                    exports: [ShowFieldPipe]
                }]
        }] });

class DbService {
    getDocuments(moduleId, pageSize, sort, cursor, filters) {
        return of([]);
    }
    getDocumentsSimple(moduleId, orderBy, filter) {
        return of([]);
    }
    getSubdocumentsSimple(moduleId, orderBy, filter) {
        return of([]);
    }
    getStateChanges(moduleId, pageSize, sort, cursor, filters) {
        return of([]);
    }
    getDocument(moduleId, documentId, stream = false) {
        return of();
    }
    setDocument(moduleId, documentId, data, options) {
        return of();
    }
    removeDocument(moduleId, documentId) {
        return of();
    }
    createUserAccount(email, password) {
        return of();
    }
    removeUserAccount(id) {
        return of();
    }
    createId() {
        return '';
    }
}

class StorageService {
    upload(path, data, metadata) { }
    getDownloadURL(ref) {
        return Promise.resolve();
    }
}

;

var FilterMethod;
(function (FilterMethod) {
    FilterMethod["Equal"] = "==";
    FilterMethod["GreaterThen"] = ">";
    FilterMethod["GreaterThenOrEqual"] = ">=";
    FilterMethod["LessThen"] = "<";
    FilterMethod["LessThenOrEqual"] = "<=";
    FilterMethod["In"] = "in";
    FilterMethod["ArrayContains"] = "array-contains";
    FilterMethod["ArrayContainsAny"] = "array-contains-any";
})(FilterMethod || (FilterMethod = {}));

const ADDITIONAL_CONTEXT = new InjectionToken('ADDITIONAL_CONTEXT');

const STORAGE_URL = new InjectionToken('STORAGE_URL');

function getHsd(key, data) {
    if (data[key]) {
        let item;
        if (typeof data[key] === 'string') {
            return of(data[key]);
        }
        switch (data[key].type) {
            case 'dynamic':
                item = safeEval(data[key].value);
                if (item) {
                    return data.form.valueChanges
                        .pipe(map(value => item(data.control.value, value)));
                }
                break;
            case 'static':
                item = safeEval(data[key].value);
                if (item) {
                    return of(item(data.control.value, data.form.getRawValue()));
                }
                break;
            case 'html':
            default:
                return of(data[key].value);
        }
    }
    return of('');
}

/**
 * Remove all characters that are not alphanumeric.
 *
 * In case of not finding any alphanumeric characters
 * return string `file-{random number}`
 *
 * @param name
 * File name to format
 */
const formatFileName = (name) => {
    const extension = name.slice(name.lastIndexOf('.'));
    name = name.slice(0, name.lastIndexOf('.'));
    name = (name.match(/[a-zA-Z\d.]/g) || []).join('');
    return !!name.length ? name + extension : `file-${Math.floor(Math.random() * 10 ** 5) + 1}${extension}`;
};

function formatGeneratedImages(data) {
    return data.reduce((acc, cur, index) => {
        acc[`generate_${index + 1}`] = Object.entries(cur).reduce((generated, [key, value]) => {
            generated += `----${key}:${value}`;
            return generated;
        }, '');
        return acc;
    }, {});
}

/**
 * Deep clones the given AbstractControl, preserving values, validators, async validators, and disabled status.
 * @param control AbstractControl
 * @returns AbstractControl
 */
function cloneAbstractControl(control, entryValue) {
    let newControl;
    if (control instanceof UntypedFormGroup) {
        const formGroup = new UntypedFormGroup({}, control.validator, control.asyncValidator);
        const controls = control.controls;
        Object.keys(controls).forEach(key => {
            formGroup.addControl(key, cloneAbstractControl(controls[key]));
        });
        newControl = formGroup;
    }
    else if (control instanceof UntypedFormArray) {
        const formArray = new UntypedFormArray([], control.validator, control.asyncValidator);
        control.controls.forEach(formControl => formArray.push(cloneAbstractControl(formControl)));
        newControl = formArray;
    }
    else if (control instanceof UntypedFormControl) {
        newControl = new UntypedFormControl(entryValue !== undefined ? entryValue : control.value, control.validator, control.asyncValidator);
    }
    else {
        throw new Error('Error: unexpected control value');
    }
    if (control.disabled) {
        newControl.disable({ emitEvent: false });
    }
    return newControl;
}

/**
 * Generated bundle index. Do not edit.
 */

export { ADDITIONAL_CONTEXT, COMPONENT_DATA, CUSTOM_COMPONENTS, CUSTOM_COMPONENT_DATA, CUSTOM_FIELDS, ConditionAction, ConditionEvaluate, ConditionType, CustomComponent, DbService, FieldComponent, FilterMethod, FormBuilderComponent, FormBuilderContextService, FormBuilderModule, FormBuilderService, Parser, ROLE, STORAGE_URL, SchemaType, SegmentComponent, ShowFieldModule, ShowFieldPipe, State, StorageService, cloneAbstractControl, compileFields, compileSegment, filterAndCompileSegments, formatFileName, formatGeneratedImages, getHsd };
